// useTaskTime.js
import { useEffect, useMemo, useState } from "react";

const fmt = (secs) => {
  const s = Math.max(0, Math.floor(secs));
  const hh = String(Math.floor(s / 3600)).padStart(2, "0");
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
};

const parse = (v) => (v ? new Date(v) : null);
const valid = (d) => d instanceof Date && !isNaN(d);

function compute(timers, now = new Date()) {
  let totalSeconds = 0;

  const ongoing =
    timers
      .filter((t) => t.stopped_at == null)
      .sort((a, b) => new Date(b.started_at) - new Date(a.started_at))[0] || null;

  for (const t of timers) {
    const start = parse(t.started_at);
    if (!valid(start)) continue;
    const end = t.stopped_at ? parse(t.stopped_at) : now;
    if (!valid(end)) continue;
    const diff = (end - start) / 1000;
    if (diff > 0) totalSeconds += diff;
  }

  const runningSince = ongoing ? parse(ongoing.started_at) : null;
  const runningElapsedSeconds = runningSince ? (now - runningSince) / 1000 : 0;

  return {
    totalSeconds: Math.floor(totalSeconds),
    totalHHMMSS: fmt(totalSeconds),
    runningTimerId: ongoing ? ongoing.id : null,
    runningSinceISO: runningSince ? runningSince.toISOString() : null,
    runningElapsedSeconds: Math.max(0, Math.floor(runningElapsedSeconds)),
    runningElapsedHHMMSS: fmt(runningElapsedSeconds),
    nowISO: now.toISOString(),
  };
}

export function useTaskTime(timers, { intervalMs = 1000 } = {}) {
  // re-compute immediately when timers array changes
  const initial = useMemo(() => compute(timers), [timers]);
  const [state, setState] = useState(initial);

  useEffect(() => {
    setState(compute(timers)); // reset when timers change
    const id = setInterval(() => setState(compute(timers)), intervalMs);
    return () => clearInterval(id);
  }, [timers, intervalMs]);

  return state; // { totalSeconds, totalHHMMSS, runningTimerId, ... }
}

export function formatSeconds(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds)); // make sure it's a non-negative integer
  const hours = String(Math.floor(s / 3600)).padStart(2, "0");
  const minutes = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const seconds = String(s % 60).padStart(2, "0");
  return `${hours}:${minutes}:${seconds}`;
}
